import { useState, useEffect, useRef } from 'react';
import useDiscussionChannel from './useDiscussionChannel';
import {
    live_discussion_get_old_messages_path,
    live_discussion_create_message_path,
    live_discussion_edit_message_path,
    live_discussion_delete_thread_path,
    live_discussion_reopen_thread_path,
    live_discussion_init_messages_path
} from '~/routes';

// Because the functionality for all discussion are the same but there UI is very different -> this hook is created to use same logic between those components.
export default function useDiscussionDialog(callbacks = {},data = {}) {
    const {target_type, scrollRef} = data
    const [messages, setMessages] = useState([]);
    const [discussionId, setDiscussionId] = useState(null);
    const [content, setContent] = useState("");
    const [usersInfo, setUsersInfo] = useState({});
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [oldestMessage, setOldestMessage] = useState(false); // If user already scroll for oldest message then don't fetch old message anymore
    const [latestMessageId, setLatestMessageId] = useState(undefined);
    const [scrollBottom, setScrollBottom] = useState(false);
    const componentRef = useRef(true);

    // target_type = project_report -> Because loaded state depends on the discussionId, if discussionId present -> loaded
    // otherwise -> there is a live state to determine if current thread is active
    const channelRef = useDiscussionChannel(discussionId, setMessages, setUsersInfo, target_type === 'project_report' ? true : data.live, componentRef);

    const setInitialInformation = (setData) => {
        setMessages((prevMessages) => [...setData.messages]);
        setDiscussionId(setData.discussion_id);
        setScrollBottom(true);
        setLoading(false);
        setUsersInfo(setData.user_info);
    }
    // Init discussion and initial messages
   useEffect(() => {
    // Because discussion in project and programme level is in bulk (many threads-> if init then many backend calls)
    if (target_type === 'project_report'){
        jQuery.getJSON(live_discussion_init_messages_path({target_type, target_id: data.target_id, discussion_type: data.discussion_type}), (res) => {
            setInitialInformation(res);
        })
    } else {
        setInitialInformation(data);
    }
    return () => {
        componentRef.current = false
    }
   }, [])

   // When new messages is load at bottom => scroll to bottom
   useEffect(() => {
    const latestId = (messages.at(-1) || {}).id
    if (latestId !== latestMessageId){
        setLatestMessageId(latestId);
        setScrollBottom(true);
    }
   }, [messages])

   useEffect(() => {
    if (scrollBottom){
        scrollRef.current?.scrollToBottom();
        setScrollBottom(false);
    }
   }, [scrollBottom])

   const handleScroll = (e) => {
    if (e.target.scrollTop === 0){
        getOlderMessages();
    }
   }

    const sendMessage = () => {
        setLoading(true);
        if (editingId !== null){
            jQuery.post(live_discussion_edit_message_path({message_id: editingId}), {content: content}, () => {
                setEditingId(null);
                reloadMessages();
                setContent('');
                setLoading(false);
            })
        } else {
            jQuery.post(live_discussion_create_message_path({discussion_id: discussionId}), {content: content}, () => {
                reloadMessages();
                setContent('');
                setLoading(false);
                setScrollBottom(true);
            })
        }
    }

    const reloadMessages = () => {
        if (channelRef.current){
            channelRef.current.reloadMessages(latestMessageId);
        }
    }

    const cancelEditing = () => {
        setEditingId(null);
        setContent('')
    }

    const getOlderMessages = () => {
        const path_params = {discussion_id: discussionId};
        if (messages.length > 0){
            path_params['old_message_id'] = messages[0].id;
        }
        if (!oldestMessage){
            setLoading(true);
            jQuery.getJSON(live_discussion_get_old_messages_path(path_params), (res) => {
                if (res.messages.length === 0){
                    setOldestMessage(true);
                }
                setMessages((prevMessages) => [...res.messages, ...prevMessages]);
                setUsersInfo(prev => ({
                    ...prev,
                    ...(res.user_info || {})
                  }))
                setLoading(false);
                // Scroll down a bit so that user scroll up more will load more messages
                scrollRef.current?.scrollTop(20)
            })
        }
    }

    const handleDeleteThread = () => {
        if (window.confirm(I18n.t('confirm_close_thread'))){
            jQuery.post(live_discussion_delete_thread_path({discussion_id: discussionId}), {}, (res) => {
                callbacks.deleteThread(res.id)
            })
        }
    }

    const handleReopenThread = () => {
        if (window.confirm(I18n.t('confirm_reopen_thread'))){
            jQuery.post(live_discussion_reopen_thread_path({discussion_id: discussionId}), {}, (res) => {
                callbacks.reopenThread(res.id)
            })   
        }
    }

    return {
        messages,
        setMessages,
        handleScroll,
        cancelEditing,
        sendMessage,
        setContent,
        content,
        loading,
        setLoading,
        usersInfo,
        reloadMessages,
        editingId,
        handleDeleteThread,
        handleReopenThread,
        setDiscussionId,
        setEditingId
    }
}